// Feature flags — org-scoped hooks backed by the consolidated settings API
// (see spec 2026-07-04-10-retire-feature-flags-api-consolidate-settings,
// which retired the standalone six-endpoint feature-flag surface in favor of
// the /settings endpoints; a feature flag is any settings code with a
// non-null `stage`, selected here via `kind=flag`). Resolution precedence and
// permissions are enforced server-side; the frontend only reads resolved
// values and lets admins manage overrides. Every exported name/signature is
// unchanged from the pre-consolidation version so consuming components
// (e.g. FeatureFlagsManagementPage) needed zero changes.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'

export type FeatureFlagSource = 'organization' | 'template' | 'server' | 'default'

export interface ResolvedFlag {
    value: unknown
    source: FeatureFlagSource
}

export interface FeatureFlagsResolvedResponse {
    flags: Record<string, ResolvedFlag>
}

export interface FeatureFlagInfo {
    code: string
    description: string
    stage: 'superadmin' | 'org' | 'server'
    default_value: unknown
    server_value?: unknown
    org_value?: unknown
    has_org_override: boolean
    resolved_value: unknown
    source: FeatureFlagSource
    last_read_at?: string | null
}

export interface FeatureFlagsManageResponse {
    flags: FeatureFlagInfo[]
}

// Wire shapes returned by the consolidated /settings endpoints (kind=flag).
interface SettingResolvedEntryWire {
    code: string
    value?: unknown
    source?: FeatureFlagSource
}

interface SettingManageEntryWire {
    code: string
    kind: 'flag' | 'setting'
    name?: Record<string, string>
    description?: string
    stage?: 'superadmin' | 'org' | 'server'
    visibility?: string
    default_value?: unknown
    server_value?: unknown
    org_value?: unknown
    has_org_override: boolean
    resolved_value: unknown
    source: FeatureFlagSource
    writable: boolean
    last_read_at?: string | null
}

const RESOLVE_STALE_MS = 5 * 60 * 1000 // 5 minutes

/**
 * useFeatureFlags resolves feature-flag values for the current organization.
 * Pass a list of codes to resolve only those; omit to resolve every registered
 * flag. Long stale time — feature flags change rarely.
 */
export function useFeatureFlags(orgCode: string, codes?: string[]) {
    return useQuery({
        queryKey: ['feature-flags', orgCode, codes ?? null],
        enabled: !!orgCode,
        staleTime: RESOLVE_STALE_MS,
        retry: 1,
        queryFn: async (): Promise<FeatureFlagsResolvedResponse> => {
            const query: Record<string, string> = { kind: 'flag' }
            if (codes && codes.length > 0) query.codes = codes.join(',')
            const response = await (
                apiClient as unknown as {
                    GET: (
                        path: string,
                        opts: {
                            params: {
                                path: { orgCode: string }
                                query?: Record<string, string>
                            }
                        }
                    ) => Promise<{ data?: { data: SettingResolvedEntryWire[] }; error?: unknown }>
                }
            ).GET('/api/v1/organizations/{orgCode}/settings', {
                params: { path: { orgCode }, query },
            })
            if (!response.data) {
                throw new Error('Failed to load feature flags')
            }
            return { flags: entriesToFlagMap(response.data.data) }
        },
    })
}

/**
 * useFeatureFlag is a convenience accessor for a single flag's resolved value.
 * Returns `fallback` while loading or when the flag is absent.
 */
export function useFeatureFlag<T = unknown>(
    orgCode: string,
    code: string,
    fallback?: T
): T {
    const { data } = useFeatureFlags(orgCode, [code])
    const flag = data?.flags?.[code]
    if (flag === undefined) return fallback as T
    return flag.value as T
}

/**
 * useManageFeatureFlags loads the admin/management detail for every flag in the
 * organization (code, description, stage, resolved value + source, override).
 */
export function useManageFeatureFlags(orgCode: string) {
    return useQuery({
        queryKey: ['feature-flags-manage', orgCode],
        enabled: !!orgCode,
        retry: 1,
        queryFn: async (): Promise<FeatureFlagsManageResponse> => {
            const response = await (
                apiClient as unknown as {
                    GET: (
                        path: string,
                        opts: { params: { path: { orgCode: string }; query?: Record<string, string> } }
                    ) => Promise<{ data?: { data: SettingManageEntryWire[] }; error?: unknown }>
                }
            ).GET('/api/v1/organizations/{orgCode}/settings/manage', {
                params: { path: { orgCode }, query: { kind: 'flag' } },
            })
            if (!response.data) {
                throw new Error('Failed to load feature flags')
            }
            return { flags: response.data.data.map(manageEntryToFlagInfo) }
        },
    })
}

/**
 * useFeatureFlagMutations exposes set/clear of an organization's flag override.
 * On success it invalidates the manage list and the resolved-value queries so
 * the UI reflects the new value immediately.
 */
export function useFeatureFlagMutations(orgCode: string) {
    const qc = useQueryClient()
    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['feature-flags-manage', orgCode] })
        qc.invalidateQueries({ queryKey: ['feature-flags', orgCode] })
    }

    const setOverride = useMutation({
        mutationFn: async (args: { code: string; value: unknown }) => {
            const response = await (
                apiClient as unknown as {
                    POST: (
                        path: string,
                        opts: {
                            params: { path: { orgCode: string; code: string } }
                            body: { value: unknown }
                        }
                    ) => Promise<{ data?: unknown; error?: unknown }>
                }
            ).POST('/api/v1/organizations/{orgCode}/settings/{code}', {
                params: { path: { orgCode, code: args.code } },
                body: { value: args.value },
            })
            if (response.error) {
                throw response.error
            }
            return response.data
        },
        onSuccess: invalidate,
    })

    const clearOverride = useMutation({
        mutationFn: async (args: { code: string }) => {
            const response = await (
                apiClient as unknown as {
                    DELETE: (
                        path: string,
                        opts: { params: { path: { orgCode: string; code: string } } }
                    ) => Promise<{ data?: unknown; error?: unknown }>
                }
            ).DELETE('/api/v1/organizations/{orgCode}/settings/{code}', {
                params: { path: { orgCode, code: args.code } },
            })
            if (response.error) {
                throw response.error
            }
            return response.data
        },
        onSuccess: invalidate,
    })

    return { setOverride, clearOverride }
}

// --- wire-shape adapters ---

function entriesToFlagMap(entries: SettingResolvedEntryWire[]): Record<string, ResolvedFlag> {
    const flags: Record<string, ResolvedFlag> = {}
    for (const entry of entries) {
        flags[entry.code] = {
            value: entry.value,
            source: entry.source ?? 'default',
        }
    }
    return flags
}

function manageEntryToFlagInfo(entry: SettingManageEntryWire): FeatureFlagInfo {
    return {
        code: entry.code,
        description: entry.description ?? '',
        stage: entry.stage ?? 'superadmin',
        default_value: entry.default_value,
        server_value: entry.server_value,
        org_value: entry.org_value,
        has_org_override: entry.has_org_override,
        resolved_value: entry.resolved_value,
        source: entry.source,
        last_read_at: entry.last_read_at,
    }
}
